import { useState, useCallback, useEffect, useRef } from "react";
import {
  TextField,
  FormControlLabel,
  Typography,
  Box,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faHandPointer,
  faSpinner,
  faArrowUpFromBracket,
  faCheck,
  faXmark,
  faUser,
  faUsers,
  faUserPlus,
  faHeadset,
  faFaceSmile,
  faBell,
  faChevronDown,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/api";
import { guardWrite, canWrite } from "../utils/permissions";
import "./liveChatSettings.css";
import "./notificationCenter.css";
import "./liveInbox.css";
import "../pages/bots.css";
import { useTranslation } from "react-i18next";
import "../translations.js";
import InfoTooltip from "./InfoTooltip";
import CroppingModal from "./croppingModal.js";
import WarningModal from "./WarningModal.js";
import { useLiveNotifications } from "./LiveNotificationsProvider";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext.js";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const DEFAULT_SCHEDULE = DAYS.reduce((acc, day) => {
  acc[day.key] = {
    enabled: ["saturday", "sunday"].includes(day.key) ? false : true,
    start: "09:00",
    end: "17:00",
  };
  return acc;
}, {});

const DEFAULT_SETTINGS = {
  availabilityMode: "manual",
  schedule: DEFAULT_SCHEDULE,
  offlineMessage:
    "We're currently offline. Please leave a message and we'll get back to you as soon as possible.",
  agentResponseTimeout: 120,
  showRequestButton: true,
  requestButtonText: "Talk to a human",
  agentDisplayName: "",
  agentAvatarUrl: "",
  agentAvatarColor: "#5616ea",
};

const TEXT_FIELD_SX = {
  "& .MuiOutlinedInput-root.Mui-focused": {
    "& fieldset": {
      border: "1px solid #515151",
    },
  },
  "& .MuiInputLabel-root": {
    color: "gray",
    "&.Mui-focused": {
      color: "#5616ea",
    },
  },
};

const ACCORDION_SX = {
  background: "transparent",
  boxShadow: "none",
  border: "none",
  borderRadius: 0,
  "&:before": { display: "none" },
  marginTop: 0,
  marginBottom: "20px",
  paddingBottom: "20px",
  borderBottom: "1.5px solid #e0e0e0",
  "&.Mui-expanded": {
    marginTop: 0,
    marginBottom: "20px",
  },
  "&.live-chat-accordion-last": {
    borderBottom: "none",
    marginBottom: 0,
    paddingBottom: 0,
    "&.Mui-expanded": {
      marginBottom: 0,
    },
  },
};

const ACCORDION_SUMMARY_SX = {
  padding: 0,
  minHeight: "unset",
  "& .MuiAccordionSummary-content": {
    margin: 0,
    "&.Mui-expanded": { margin: 0 },
  },
  "&.Mui-expanded": { minHeight: "unset" },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "#5616ea",
    fontSize: "18px",
  },
};

const ACCORDION_DETAILS_SX = {
  padding: 0,
  paddingTop: "10px",
};

const DEFAULT_AVATARS = [
  {
    id: "preset:person",
    labelKey: "live_chat_avatar_person",
    icon: faUser,
    svgTemplate: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="{{COLOR}}"/><g transform="translate(14,12) scale(0.08)"><path fill="#fff" d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"/></g></svg>',
  },
  {
    id: "preset:headset",
    labelKey: "live_chat_avatar_support",
    icon: faHeadset,
    svgTemplate: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="{{COLOR}}"/><g transform="translate(12,12) scale(0.078125)"><path fill="#fff" d="M256 48C141.1 48 48 141.1 48 256v40c0 13.3-10.7 24-24 24s-24-10.7-24-24V256C0 114.6 114.6 0 256 0S512 114.6 512 256V400.1c0 48.6-39.4 88-88.1 88L313.6 488c-8.3 14.3-23.8 24-41.6 24H240c-26.5 0-48-21.5-48-48s21.5-48 48-48h32c17.8 0 33.3 9.7 41.6 24l110.4 .1c22.1 0 40-17.9 40-40V256c0-114.9-93.1-208-208-208zM144 208h16c17.7 0 32 14.3 32 32V352c0 17.7-14.3 32-32 32H144c-35.3 0-64-28.7-64-64V272c0-35.3 28.7-64 64-64zm224 0c35.3 0 64 28.7 64 64v48c0 35.3-28.7 64-64 64H352c-17.7 0-32-14.3-32-32V240c0-17.7 14.3-32 32-32h16z"/></g></svg>',
  },
  {
    id: "preset:smile",
    labelKey: "live_chat_avatar_friendly",
    icon: faFaceSmile,
    svgTemplate: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="{{COLOR}}"/><g transform="translate(12,12) scale(0.078125)"><path fill="#fff" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM164.1 325.5C182 346.2 212.6 368 256 368s74-21.8 91.9-42.5c5.8-6.7 15.9-7.4 22.6-1.6s7.4 15.9 1.6 22.6C349.8 372.1 311.1 400 256 400s-93.8-27.9-116.1-53.5c-5.8-6.7-5.1-16.8 1.6-22.6s16.8-5.1 22.6 1.6zM144.4 208a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm192-32a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></g></svg>',
  },
];

const getAvatarSvg = (presetId, color = "#5616ea") => {
  const preset = DEFAULT_AVATARS.find((a) => a.id === presetId);
  if (!preset) return null;
  return "data:image/svg+xml," + encodeURIComponent(preset.svgTemplate.replace("{{COLOR}}", color));
};

const getAvatarSrc = (avatarUrl, color = "#5616ea") => {
  if (!avatarUrl) return null;
  const preset = DEFAULT_AVATARS.find((a) => a.id === avatarUrl);
  return preset ? getAvatarSvg(avatarUrl, color) : avatarUrl;
};

const LiveChatSettings = ({ botId, onUnsavedStateChange, onUnsavedHandlersChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registerUnsavedChangesGuard } = useUnsavedChanges();

  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [initialSettings, setInitialSettings] = useState({ ...DEFAULT_SETTINGS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  // Avatar upload state
  const agentAvatarInputRef = useRef(null);
  const [, setUploadedAgentAvatarFile] = useState(null);
  const [currentCroppingTarget, setCurrentCroppingTarget] = useState(null);
  const [tempFile, setTempFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);

  const mapResponseToSettings = (data) => ({
    availabilityMode: data.availability_mode || "manual",
    schedule: Object.keys(data.schedule || {}).length > 0 ? data.schedule : DEFAULT_SCHEDULE,
    offlineMessage: data.offline_message || DEFAULT_SETTINGS.offlineMessage,
    agentResponseTimeout: data.agent_response_timeout || 120,
    showRequestButton: data.show_request_button != null ? Boolean(data.show_request_button) : true,
    requestButtonText: data.request_button_text || DEFAULT_SETTINGS.requestButtonText,
    agentDisplayName: data.agent_display_name || "",
    agentAvatarUrl: data.agent_avatar_url || "",
    agentAvatarColor: data.agent_avatar_color || "#5616ea",
  });

  const mapSettingsToPayload = (s) => ({
    availability_mode: s.availabilityMode,
    schedule: s.schedule,
    offline_message: s.offlineMessage,
    agent_response_timeout: s.agentResponseTimeout,
    show_request_button: s.showRequestButton,
    request_button_text: s.requestButtonText,
    agent_display_name: s.agentDisplayName,
    agent_avatar_url: s.agentAvatarUrl,
    agent_avatar_color: s.agentAvatarColor,
  });

  const fetchSettings = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}live_chat_settings/${id}`
      );
      if (!res || !res.ok) throw new Error(`HTTP error! Status: ${res?.status}`);
      const data = await res.json();
      const mapped = mapResponseToSettings(data);
      setSettings(mapped);
      setInitialSettings(mapped);
      setHasLoadedInitial(true);
    } catch (err) {
      console.error("Error fetching live chat settings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (botId) fetchSettings(botId);
  }, [botId, fetchSettings]);

  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Persist accordion open/close state per bot across tab switches.
  const accordionStorageKey = botId ? `liveChatAccordions:${botId}` : null;
  const DEFAULT_EXPANDED_ACCORDIONS = { notifications: true, currentStatus: true, liveAgent: true, agentIdentity: true };
  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    if (!accordionStorageKey) return { ...DEFAULT_EXPANDED_ACCORDIONS };
    try {
      const raw = localStorage.getItem(accordionStorageKey);
      if (raw) return JSON.parse(raw) || { ...DEFAULT_EXPANDED_ACCORDIONS };
    } catch (_e) { /* noop */ }
    return { ...DEFAULT_EXPANDED_ACCORDIONS };
  });

  useEffect(() => {
    if (!accordionStorageKey) return;
    try {
      localStorage.setItem(accordionStorageKey, JSON.stringify(expandedAccordions));
    } catch (_e) { /* noop */ }
  }, [accordionStorageKey, expandedAccordions]);

  const toggleAccordion = (key) => (_e, isExpanded) => {
    setExpandedAccordions((prev) => ({ ...prev, [key]: isExpanded }));
  };

  const [timeoutInput, setTimeoutInput] = useState("");

  const TIMEOUT_MIN = 30;
  const TIMEOUT_MAX = 500;

  useEffect(() => {
    setTimeoutInput(String(settings.agentResponseTimeout ?? ""));
  }, [settings.agentResponseTimeout]);

  const commitTimeout = () => {
    const parsed = parseInt(timeoutInput, 10);
    if (Number.isNaN(parsed)) {
      setTimeoutInput(String(settings.agentResponseTimeout));
      return;
    }
    const clamped = Math.min(TIMEOUT_MAX, Math.max(TIMEOUT_MIN, parsed));
    setTimeoutInput(String(clamped));
    if (clamped !== settings.agentResponseTimeout) {
      updateSetting("agentResponseTimeout", clamped);
    }
  };

  const updateScheduleDay = useCallback((dayKey, field, value) => {
    setSettings((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [dayKey]: {
          ...prev.schedule[dayKey],
          [field]: value,
        },
      },
    }));
  }, []);

  // Avatar upload handlers
  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setTempFile(file);
    setCurrentCroppingTarget("agentAvatar");
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCancelCrop = () => {
    setTempFile(null);
    setImageSrc(null);
    setCurrentCroppingTarget(null);
    if (agentAvatarInputRef.current) agentAvatarInputRef.current.value = null;
  };

  const resizeImage = (base64, maxSize) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = base64;
    });

  const handleConfirmCrop = async (croppedBase64) => {
    const resized = await resizeImage(croppedBase64, 200);
    updateSetting("agentAvatarUrl", resized);
    setUploadedAgentAvatarFile(tempFile);
    if (agentAvatarInputRef.current) agentAvatarInputRef.current.value = null;
    setTempFile(null);
    setImageSrc(null);
    setCurrentCroppingTarget(null);
  };

  const handleRemoveAvatar = () => {
    updateSetting("agentAvatarUrl", "");
    setUploadedAgentAvatarFile(null);
    if (agentAvatarInputRef.current) agentAvatarInputRef.current.value = null;
  };

  const base64ToFile = (base64, filename) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8 = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    return new File([u8], filename, { type: mime });
  };

  const uploadImage = async (base64, type) => {
    const fd = new FormData();
    fd.append("file", base64ToFile(base64, `${type}-${Date.now()}.png`));
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}upload_image/`, {
      method: "POST",
      body: fd,
    });
    if (!res.ok) throw new Error(`Failed to upload ${type}. Status: ${res.status}`);
    const j = await res.json();
    return j.file_path;
  };

  // Per-bot agent online/offline status. Lifted from LiveInbox / bots.js so
  // the toggle is reachable directly from the Live Chat settings page.
  const [agentOnline, setAgentOnline] = useState(false);
  const [agentStatusLoading, setAgentStatusLoading] = useState(true);
  const [togglingAgentOnline, setTogglingAgentOnline] = useState(false);
  // Full team roster for this bot — powers the chip + modal that mirror
  // the bots.js team availability UI. Loaded from /live/agents/{botId}
  // and refreshed every 30s so the chip count stays accurate.
  const [roster, setRoster] = useState(null);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  // Per-teammate "forcing offline" busy flag, keyed by user_id.
  const [forcingOffline, setForcingOffline] = useState({});
  // Pending confirmation for force-offlining a teammate. When set the
  // shared WarningModal is shown to confirm the destructive action.
  const [pendingForceOffline, setPendingForceOffline] = useState(null);
  // Provider's heartbeat registrar — keeps the agent flagged online on the
  // server every 30s so the backend reaper doesn't flip the row to offline
  // after 90s of silence. Without this call, the toggle on the settings page
  // would set status='online' once and the agent would silently drop to
  // offline a couple of minutes later.
  const { setAgentOnline: registerAgentHeartbeat } = useLiveNotifications();

  useEffect(() => {
    if (!botId) return;
    let cancelled = false;
    setAgentStatusLoading(true);
    (async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}live/agent_status`
        );
        if (!res || !res.ok) return;
        const data = await res.json();
        const status = (data.statuses || {})[botId];
        const online = status === true || status === 'online';
        if (!cancelled) {
          setAgentOnline(online);
          // Seed the heartbeat ticker for this bot if the server already
          // considers us online — mirrors the same logic in LiveInbox.
          if (online) registerAgentHeartbeat(botId, true);
        }
      } catch (_e) { /* noop */ } finally {
        if (!cancelled) setAgentStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [botId, registerAgentHeartbeat]);

  // Fetch the full team roster for this bot and poll every 30s,
  // mirroring the logic in bots.js so the chip count stays in sync.
  useEffect(() => {
    if (!botId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}live/agents/${botId}`
        );
        if (!res || !res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setRoster(data.agents || []);
        setRosterLoaded(true);
      } catch (_e) { /* noop */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [botId]);

  // Force a teammate offline. Same permission gate + endpoint as
  // LiveInbox / bots.js.
  const handleForceTeammateOffline = async (userId) => {
    if (!canWrite()) return;
    setForcingOffline((prev) => ({ ...prev, [userId]: true }));
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}live/agent_status/${botId}/user/${userId}`,
        { method: 'PUT', body: JSON.stringify({ status: 'offline' }) }
      );
      if (res && res.ok) {
        setRoster((prev) => (prev || []).map((a) =>
          a.user_id === userId ? { ...a, online: false } : a
        ));
      }
    } catch (_) { /* swallow */ }
    setForcingOffline((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const handleToggleAgentOnline = async () => {
    if (!botId || togglingAgentOnline) return;
    const next = agentOnline ? 'offline' : 'online';
    setTogglingAgentOnline(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}live/agent_status/${botId}`,
        { method: 'PUT', body: JSON.stringify({ status: next }) }
      );
      if (res && res.ok) {
        setAgentOnline(next === 'online');
        // Keep the local roster in sync so the chip count updates
        // immediately instead of waiting for the next 30s poll.
        setRoster((prev) => (prev || []).map((a) =>
          a.is_self ? { ...a, online: next === 'online' } : a
        ));
        // Start (or stop) the per-bot heartbeat ticker so the server-side
        // reaper doesn't silently flip the agent back to offline.
        registerAgentHeartbeat(botId, next === 'online');
      }
    } catch (err) {
      console.error('Error toggling agent online status:', err);
    } finally {
      setTogglingAgentOnline(false);
    }
  };

  const hasUnsavedChanges =
    hasLoadedInitial && JSON.stringify(settings) !== JSON.stringify(initialSettings);

  const handleSave = async () => {
    // Read-only teammates: revert local edits and surface the shared
    // permission modal instead of attempting the PUT (which would 403).
    if (!guardWrite(() => handleDiscardChanges())) return;
    setSaving(true);
    try {
      // Upload avatar if it's a new base64 image
      let avatarUrl = settings.agentAvatarUrl;
      if (avatarUrl && avatarUrl.startsWith("data:image/")) {
        avatarUrl = await uploadImage(avatarUrl, "agent-avatar");
      }
      const updatedSettings = { ...settings, agentAvatarUrl: avatarUrl };
      const payload = mapSettingsToPayload(updatedSettings);
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}live_chat_settings/${botId}`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        }
      );
      if (!res || !res.ok) throw new Error(`HTTP error! Status: ${res?.status}`);
      setSettings(updatedSettings);
      setInitialSettings({ ...updatedSettings });
      setUploadedAgentAvatarFile(null);
      // Notify LiveNotificationsProvider so it re-fetches in-app prefs
      // for this bot without requiring a page reload.
      try {
        window.dispatchEvent(
          new CustomEvent("live-chat-prefs-updated", { detail: { botId } })
        );
      } catch (_e) { /* noop */ }
      return true;
    } catch (err) {
      console.error("Error saving live chat settings:", err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setSettings({ ...initialSettings });
  };

  useEffect(() => {
    if (onUnsavedStateChange) onUnsavedStateChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedStateChange]);

  // Keep latest handlers in a ref so guard registration doesn't re-run on every keystroke.
  const unsavedHandlersRef = useRef({ save: handleSave, discard: handleDiscardChanges });
  unsavedHandlersRef.current = { save: handleSave, discard: handleDiscardChanges };

  useEffect(() => {
    if (!onUnsavedHandlersChange) return undefined;
    onUnsavedHandlersChange({
      save: (...args) => unsavedHandlersRef.current.save(...args),
      discard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
    return () => onUnsavedHandlersChange(null);
  }, [onUnsavedHandlersChange]);

  useEffect(() => {
    return registerUnsavedChangesGuard(`live-chat-settings-${botId}`, {
      when: hasUnsavedChanges,
      onSave: (...args) => unsavedHandlersRef.current.save(...args),
      onDiscard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
  }, [botId, hasUnsavedChanges, registerUnsavedChangesGuard]);

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="live-chat-parent-container">
      <div className="live-chat-container">
        {/* Current Status (per-agent presence) */}
        <Accordion
          expanded={!!expandedAccordions.currentStatus}
          onChange={toggleAccordion("currentStatus")}
          disableGutters
          elevation={0}
          sx={ACCORDION_SX}
        >
          <AccordionSummary
            expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
            sx={ACCORDION_SUMMARY_SX}
          >
            <h4 className="widget-sub-header" style={{ margin: 0 }}>
              {t("live_chat_current_status_header") || "Current status"}
              <InfoTooltip text={t("live_chat_current_status_tooltip") || "Your own availability for this agent. While you are online, visitors can request a human and the AI can hand the conversation over to you. Your status stays exactly as you set it until you change it."} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
            <Box className="control-group" style={{ marginBottom: '0px' }}>
              {(agentStatusLoading || !rosterLoaded) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', color: '#666' }}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                </Box>
              ) : (
                <>
                  {(() => {
                    const teamHasOtherOnline = !!(roster && roster.some((a) => !a.is_self && a.online));
                    let descKey;
                    let descFallback;
                    if (agentOnline) {
                      descKey = 'live_inbox_status_desc_online';
                      descFallback = 'Visitors can request a human agent to join the chat.';
                    } else if (teamHasOtherOnline) {
                      descKey = 'live_inbox_status_desc_offline_team_online';
                      descFallback = 'You appear offline, but at least one teammate is online and can be requested by visitors.';
                    } else {
                      descKey = 'live_inbox_status_desc_offline';
                      descFallback = 'Visitors cannot currently request a human agent.';
                    }
                    return (
                      <span
                        key={descKey}
                        className="live-inbox-bot-desc live-chat-status-desc-fade"
                        style={{ display: 'block', marginBottom: '12px' }}
                      >
                        {t(descKey) || descFallback}
                      </span>
                    );
                  })()}
                  <div className="agent-availability-controls">
                    <button
                      type="button"
                      className={`live-inbox-toggle ${agentOnline ? 'online' : 'offline'}`}
                      onClick={handleToggleAgentOnline}
                      disabled={togglingAgentOnline}
                      style={{ width: 'auto' }}
                    >
                      {togglingAgentOnline ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={agentOnline ? faToggleOn : faToggleOff} />
                      )}
                      <span>
                        {agentOnline
                          ? (t('live_inbox_online') || 'Online')
                          : (t('live_inbox_offline') || 'Offline')}
                      </span>
                    </button>
                    {roster && roster.length >= 1 && (() => {
                      const totalAgents = roster.length;
                      const onlineCount = roster.filter((a) => a.online).length;
                      return (
                        <button
                          type="button"
                          className={`agent-team-chip ${onlineCount > 0 ? 'has-online' : 'all-offline'}`}
                          onClick={() => setRosterModalOpen(true)}
                          title={t('bots_team_availability_tooltip') || 'Show team availability'}
                        >
                          <FontAwesomeIcon icon={faUsers} />
                          <span>{onlineCount}/{totalAgents}</span>
                        </button>
                      );
                    })()}
                  </div>
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={!!expandedAccordions.notifications}
          onChange={toggleAccordion("notifications")}
          disableGutters
          elevation={0}
          sx={ACCORDION_SX}
        >
          <AccordionSummary
            expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
            sx={ACCORDION_SUMMARY_SX}
          >
            <h4 className="widget-sub-header" style={{ margin: 0 }}>
              {t("live_inbox_notifications_title") || "Notifications"}
              <InfoTooltip text={t("live_inbox_notifications_tooltip") || "Manage how to get notified about new chats and other live chat events."} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
            <Box className="control-group" style={{ marginBottom: '0px' }}>
              <span className="live-inbox-bot-desc" style={{ display: 'block', marginBottom: '12px' }}>
                {t("live_inbox_notifications_desc") || "Manage how to get notified about new chats and more."}
              </span>
              <button
                type="button"
                className="live-inbox-toggle online"
                style={{ width: 'auto', alignSelf: 'flex-start' }}
                onClick={() => window.dispatchEvent(new CustomEvent('bot-settings-set-tab', { detail: { tab: 'notifications' } }))}
              >
                <FontAwesomeIcon icon={faBell} />
                <span>{t('live_inbox_manage_notifications') || 'Manage Notifications'}</span>
              </button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Cropping Modal */}
        <CroppingModal
          isOpen={Boolean(currentCroppingTarget)}
          src={imageSrc}
          onCancel={handleCancelCrop}
          onConfirm={handleConfirmCrop}
        />

        {/* 1. Online / Offline (per-agent presence) */}
        <Accordion
          expanded={!!expandedAccordions.liveAgent}
          onChange={toggleAccordion("liveAgent")}
          disableGutters
          elevation={0}
          sx={ACCORDION_SX}
        >
          <AccordionSummary
            expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
            sx={ACCORDION_SUMMARY_SX}
          >
            <h4 className="widget-sub-header" style={{ margin: 0 }}>
              {t("live_chat_enable_header")}
              <InfoTooltip text={t("live_chat_enable_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Box className="control-group" sx={{ marginBottom: '10px', marginTop: '0px' }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="textPrimary"
              sx={{ marginBottom: "12px", display: "flex", alignItems: "center" }}
            >
              {t("live_chat_availability_header")}
            </Typography>

            <div className="availability-mode-selector">
              <div
                className={`availability-mode-card ${settings.availabilityMode === "manual" ? "selected" : ""}`}
                onClick={() => updateSetting("availabilityMode", "manual")}
              >
                <div className="availability-mode-card-header">
                  <FontAwesomeIcon
                    icon={faHandPointer}
                    className="availability-mode-card-icon"
                  />
                  <span className="availability-mode-card-title">
                    {t("live_chat_mode_manual")}
                  </span>
                </div>
                <span className="availability-mode-card-description">
                  {t("live_chat_mode_manual_desc")}
                </span>
              </div>

              <div
                className="availability-mode-card availability-mode-card-disabled"
                title={t("coming_soon") || "Coming soon"}
                aria-disabled="true"
              >
                <div className="availability-mode-card-header">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="availability-mode-card-icon"
                  />
                  <span className="availability-mode-card-title">
                    {t("live_chat_mode_scheduled")}
                  </span>
                  <span className="availability-mode-card-badge">
                    {t("coming_soon") || "Coming soon"}
                  </span>
                </div>
                <span className="availability-mode-card-description">
                  {t("live_chat_mode_scheduled_desc")}
                </span>
              </div>
            </div>

            {/* Schedule Grid - only for scheduled mode */}
            {false && settings.availabilityMode === "scheduled" && (
              <div className="schedule-grid">
                <div className="schedule-timezone-bar">
                  <FontAwesomeIcon icon={faClock} className="schedule-timezone-icon" />
                  <Typography variant="body2" fontWeight={500}>
                    {t("live_chat_timezone_hint")}{" "}
                    <strong>{localStorage.getItem("timezone") || "UTC"}</strong>
                  </Typography>
                  <span
                    className="schedule-timezone-link"
                    onClick={() => navigate("/my-account")}
                  >
                    {t("live_chat_adjust_timezone")}
                  </span>
                </div>
                {DAYS.map((day) => {
                  const daySettings = settings.schedule[day.key];
                  return (
                    <div
                      key={day.key}
                      className={`schedule-row ${!daySettings.enabled ? "schedule-row-disabled" : ""}`}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={daySettings.enabled}
                            onChange={(e) =>
                              updateScheduleDay(
                                day.key,
                                "enabled",
                                e.target.checked
                              )
                            }
                            size="small"
                            sx={{
                              "&.Mui-checked": { color: "#5616ea" },
                              "&:hover": { backgroundColor: "#5616ea15" },
                              padding: "4px",
                            }}
                          />
                        }
                        label=""
                        sx={{ marginRight: 0 }}
                      />
                      <span className="schedule-day-label">{day.label}</span>
                      <div className="schedule-times">
                        <div className="schedule-time-dropdown">
                          <select
                            className="time-select"
                            value={daySettings.start.split(":")[0]}
                            onChange={(e) =>
                              updateScheduleDay(
                                day.key,
                                "start",
                                `${e.target.value}:${daySettings.start.split(":")[1]}`
                              )
                            }
                          >
                            {HOURS.map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <span className="time-colon">:</span>
                          <select
                            className="time-select"
                            value={daySettings.start.split(":")[1]}
                            onChange={(e) =>
                              updateScheduleDay(
                                day.key,
                                "start",
                                `${daySettings.start.split(":")[0]}:${e.target.value}`
                              )
                            }
                          >
                            {MINUTES.map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                        <span className="schedule-separator">—</span>
                        <div className="schedule-time-dropdown">
                          <select
                            className="time-select"
                            value={daySettings.end.split(":")[0]}
                            onChange={(e) =>
                              updateScheduleDay(
                                day.key,
                                "end",
                                `${e.target.value}:${daySettings.end.split(":")[1]}`
                              )
                            }
                          >
                            {HOURS.map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <span className="time-colon">:</span>
                          <select
                            className="time-select"
                            value={daySettings.end.split(":")[1]}
                            onChange={(e) =>
                              updateScheduleDay(
                                day.key,
                                "end",
                                `${daySettings.end.split(":")[0]}:${e.target.value}`
                              )
                            }
                          >
                            {MINUTES.map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Box>

        {/* Widget Request Button */}
        <Box className="control-group" sx={{ marginBottom: '0px', marginTop: '16px' }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="textPrimary"
              sx={{ marginBottom: "16px", display: "flex", alignItems: "center" }}
            >
              {t("live_chat_request_button_header")}
            </Typography>
            <TextField
              value={settings.requestButtonText}
              onChange={(e) =>
                updateSetting("requestButtonText", e.target.value)
              }
              fullWidth
              variant="outlined"
              label={t("live_chat_request_button_text_label")}
              placeholder={t("live_chat_request_button_text_placeholder")}
              inputProps={{ maxLength: 40 }}
              helperText={`${settings.requestButtonText.length}/40`}
              FormHelperTextProps={{
                sx: {
                  textAlign: 'right',
                  mr: 0,
                  color: settings.requestButtonText.length === 40 ? 'error.main' : 'text.secondary',
                },
              }}
              sx={{ ...TEXT_FIELD_SX, maxWidth: 400 }}
            />
          </Box>

        {/* Agent Response Timeout */}
        <Box className="control-group" sx={{ marginBottom: '0px', marginTop: '0px' }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="textPrimary"
              sx={{ marginBottom: "0px", display: "flex", alignItems: "center" }}
            >
              {t("live_chat_agent_timeout_label")}
            </Typography>

            <TextField
              type="number"
              value={timeoutInput}
              onChange={(e) => setTimeoutInput(e.target.value)}
              onBlur={commitTimeout}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitTimeout();
                  e.target.blur();
                }
              }}
              fullWidth
              variant="outlined"
              label={t("live_chat_agent_timeout_seconds_label")}
              inputProps={{ min: TIMEOUT_MIN, max: TIMEOUT_MAX }}
              InputProps={{
                endAdornment: (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ whiteSpace: "nowrap", ml: 0.5 }}
                  >
                    {t("live_chat_agent_timeout_seconds_adornment")}
                  </Typography>
                ),
              }}
              helperText={t("live_chat_agent_timeout_minmax", { min: TIMEOUT_MIN, max: TIMEOUT_MAX })}
              FormHelperTextProps={{
                sx: {
                  textAlign: 'right',
                  mr: 0,
                  color: 'text.secondary',
                },
              }}
              sx={{ ...TEXT_FIELD_SX, maxWidth: 400, marginTop: "16px" }}
            />
          </Box>
          </AccordionDetails>
        </Accordion>

        {/* 2. Agent Identity */}
        <Accordion
          expanded={!!expandedAccordions.agentIdentity}
          onChange={toggleAccordion("agentIdentity")}
          disableGutters
          elevation={0}
          className="live-chat-accordion-last"
          sx={ACCORDION_SX}
        >
          <AccordionSummary
            expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
            sx={ACCORDION_SUMMARY_SX}
          >
            <h4 className="widget-sub-header" style={{ margin: 0 }}>
              {t("live_chat_agent_identity_header")}
              <InfoTooltip text={t("live_chat_agent_identity_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
            <Box className="control-group" sx={{ marginTop: '0px', marginBottom: '0px' }}>

              <Typography
                variant="body2"
                fontWeight={600}
                color="textPrimary"
                sx={{ marginBottom: "14px", display: "flex", alignItems: "center" }}
              >
                {t("live_chat_agent_display_name_header")}
              </Typography>

              <TextField
                value={settings.agentDisplayName}
                onChange={(e) =>
                  updateSetting("agentDisplayName", e.target.value)
                }
                variant="outlined"
                label={t("live_chat_agent_display_name_label")}
                placeholder={t("live_chat_agent_display_name_placeholder")}
                inputProps={{ maxLength: 100 }}
                helperText={`${settings.agentDisplayName.length}/100`}
                FormHelperTextProps={{
                  sx: {
                    textAlign: 'right',
                    mr: 0,
                    color: settings.agentDisplayName.length === 100 ? 'error.main' : 'text.secondary',
                  },
                }}
                sx={{ ...TEXT_FIELD_SX, maxWidth: 400, mb: 0 }}
              />

              <div className="agent-avatar-selector">
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="textPrimary"
                  sx={{ marginTop: "0px", marginBottom: "16px", display: "flex", alignItems: "center" }}
                >
                  {t("live_chat_agent_avatar_header")}
                </Typography>
                <div className="agent-avatar-options">
                  {DEFAULT_AVATARS.map((avatar) => {
                    const isSelected = settings.agentAvatarUrl === avatar.id;
                    return (
                      <div
                        key={avatar.id}
                        className={`agent-avatar-option ${isSelected ? "selected" : ""}`}
                        style={{ backgroundColor: settings.agentAvatarColor }}
                        onClick={() => {
                          if (isSelected) {
                            updateSetting("agentAvatarUrl", "");
                          } else {
                            updateSetting("agentAvatarUrl", avatar.id);
                            setUploadedAgentAvatarFile(null);
                            if (agentAvatarInputRef.current) agentAvatarInputRef.current.value = null;
                          }
                        }}
                        title={isSelected ? t("live_chat_avatar_click_deselect") : t(avatar.labelKey)}
                      >
                        <FontAwesomeIcon icon={avatar.icon} />
                        {isSelected && (
                          <span className="agent-avatar-checkmark">
                            <FontAwesomeIcon icon={faCheck} className="checkmark-icon" />
                            <FontAwesomeIcon icon={faXmark} className="xmark-icon" />
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <div
                    className="agent-avatar-option agent-avatar-upload-btn"
                    onClick={() => agentAvatarInputRef.current?.click()}
                    title={t("live_chat_avatar_upload")}
                  >
                    <FontAwesomeIcon icon={faArrowUpFromBracket} />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={agentAvatarInputRef}
                    onChange={handleAvatarUpload}
                    style={{ display: "none" }}
                  />
                  {settings.agentAvatarUrl && !settings.agentAvatarUrl.startsWith("preset:") && (
                    <div
                      className="agent-avatar-option agent-avatar-custom selected"
                      onClick={handleRemoveAvatar}
                      title={t("live_chat_avatar_click_remove")}
                    >
                      <img src={settings.agentAvatarUrl} alt={t("live_chat_avatar_custom_alt")} />
                      <span className="agent-avatar-checkmark">
                        <FontAwesomeIcon icon={faCheck} className="checkmark-icon" />
                        <FontAwesomeIcon icon={faXmark} className="xmark-icon" />
                      </span>
                    </div>
                  )}
                </div>

                <div className="agent-avatar-color-picker">
                  <label htmlFor="agentAvatarColor">{t("live_chat_agent_avatar_color_label")}</label>
                  <input
                    id="agentAvatarColor"
                    type="color"
                    value={settings.agentAvatarColor}
                    onChange={(e) => updateSetting("agentAvatarColor", e.target.value)}
                  />
                </div>
              </div>


            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Notifications matrix moved to the dedicated Notification Center tab. */}

        {/* Save / Discard */}
        {hasUnsavedChanges && (
          <div className="unsaved-changes-message">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">{t("unsaved_changes_info")}</div>
          </div>
        )}
        <div className="save-discard-buttons">
          <button className="save-widget-btn" onClick={handleSave} disabled={saving}>
            {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : t("save")}
          </button>
          {hasUnsavedChanges && (
            <button
              onClick={handleDiscardChanges}
              className="discard-widget-btn"
            >
              {t("discard_changes")}
            </button>
          )}
        </div>
      </div>

      {/* Team availability modal — opens from the team chip next to the
          Online/Offline toggle. Same structure as bots.js so the UX is
          identical across the bot card and Live Agent settings tab. */}
      {rosterModalOpen && roster && (() => {
        const sorted = [...roster].sort((a, b) => {
          if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
          if (a.online !== b.online) return a.online ? -1 : 1;
          const an = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || '';
          const bn = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || '';
          return an.localeCompare(bn);
        });
        const onlineCount = sorted.filter((a) => a.online).length;
        const selfAgent = sorted.find((a) => a.is_self);
        const otherAgents = sorted.filter((a) => !a.is_self);
        const renderRow = (a) => {
          const displayName = (a.first_name || a.last_name)
            ? `${a.first_name || ''} ${a.last_name || ''}`.trim()
            : (a.email || `User #${a.user_id}`);
          const isSelf = a.is_self;
          const canForceOff = !isSelf && a.online && canWrite();
          const interactive = isSelf || canForceOff;
          const busy = isSelf
            ? togglingAgentOnline
            : !!forcingOffline[a.user_id];
          const onClick = () => {
            if (!interactive || busy) return;
            if (isSelf) {
              handleToggleAgentOnline();
            } else {
              setPendingForceOffline({
                userId: a.user_id,
                name: displayName,
              });
            }
          };
          return (
            <li key={a.user_id} className="live-inbox-agent-row">
              <span className={`live-inbox-agent-dot ${a.online ? 'online' : 'offline'}`} />
              <span className="live-inbox-agent-name" title={a.email || ''}>
                {displayName}
              </span>
              <MuiTooltip
                title={
                  interactive
                    ? (a.online
                        ? (t('live_inbox_team_set_offline') || 'Set offline')
                        : (t('live_inbox_team_set_online') || 'Set online'))
                    : (isSelf
                        ? ''
                        : (t('live_inbox_team_toggle_disabled') || 'Only this teammate can set themselves online.'))
                }
                arrow
                placement="top"
                enterDelay={300}
                enterNextDelay={150}
                disableInteractive
              >
                <span className="live-inbox-toggle-wrap">
                  <button
                    type="button"
                    className={`live-inbox-toggle ${a.online ? 'online' : 'offline'}${interactive ? '' : ' is-disabled'}`}
                    onClick={onClick}
                    disabled={!interactive || busy}
                    aria-disabled={!interactive || busy}
                  >
                    {busy ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={a.online ? faToggleOn : faToggleOff} />
                    )}
                    <span>{a.online
                      ? (t('live_inbox_online') || 'Online')
                      : (t('live_inbox_offline') || 'Offline')
                    }</span>
                  </button>
                </span>
              </MuiTooltip>
            </li>
          );
        };
        return (
          <div className="agent-team-modal-overlay" onClick={() => setRosterModalOpen(false)}>
            <div className="agent-team-modal" onClick={(e) => e.stopPropagation()}>
              <div className="agent-team-modal-header">
                <div className="live-inbox-availability-title-block">
                  <span className="live-inbox-availability-title live-inbox-card-title">
                    <span>{t('bots_team_availability_title') || 'Team availability'}</span>
                    <InfoTooltip text={t('live_inbox_availability_tooltip') || 'Live agents who are online can be requested by visitors directly from the chat. You can set yourself online or offline at any time, and admins can take a teammate offline if needed.'} />
                  </span>
                  <span className="live-inbox-availability-desc">
                    {onlineCount > 0
                      ? (t('live_inbox_availability_desc_some_online') || 'At least one agent is online. Visitors can request a human agent.')
                      : (t('live_inbox_availability_desc_all_offline') || 'All agents are offline. Visitors cannot request a human agent.')
                    }
                  </span>
                </div>
                <span className="live-inbox-availability-count">
                  {onlineCount}/{sorted.length} {t('live_inbox_online') || 'online'}
                </span>
                <button
                  type="button"
                  className="agent-team-modal-close"
                  onClick={() => setRosterModalOpen(false)}
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="agent-team-modal-body">
                {selfAgent && (
                  <div className="live-inbox-agent-section">
                    <div className="live-inbox-agent-section-header">
                      {t('live_inbox_my_availability') || 'My availability'}
                    </div>
                    <ul className="live-inbox-agent-list">
                      {renderRow(selfAgent)}
                    </ul>
                  </div>
                )}
                <div className="live-inbox-agent-section-divider" />
                <div className="live-inbox-agent-section">
                  <div className="live-inbox-agent-section-header">
                    {t('live_inbox_other_agents') || 'Other agents'}
                  </div>
                  {otherAgents.length === 0 ? (
                    <div className="live-inbox-agent-empty-block">
                      <div className="live-inbox-agent-empty">
                        {t('live_inbox_no_other_agents') || 'No other agents in this organization yet.'}
                      </div>
                      {canWrite() && (
                        <button
                          type="button"
                          className="live-inbox-invite-btn"
                          onClick={() => {
                            setRosterModalOpen(false);
                            navigate('/team-members');
                          }}
                        >
                          <FontAwesomeIcon icon={faUserPlus} />
                          <span>{t('live_inbox_invite_team_members') || 'Invite team members'}</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <ul className={`live-inbox-agent-list${otherAgents.length > 3 ? ' is-scrollable' : ''}`}>
                      {otherAgents.map(renderRow)}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <WarningModal
        open={!!pendingForceOffline}
        onClose={() => setPendingForceOffline(null)}
        title={t('force_offline_title') || 'Set teammate offline?'}
        cancelText={t('cancel') || 'Cancel'}
        confirmText={t('force_offline_confirm') || 'Set offline'}
        danger
        onConfirm={() => {
          if (!pendingForceOffline) return;
          const { userId } = pendingForceOffline;
          setPendingForceOffline(null);
          handleForceTeammateOffline(userId);
        }}
      >
        {pendingForceOffline && (
          <>
            {t('force_offline_body_prefix') || 'Are you sure you want to set'}{' '}
            <strong>{pendingForceOffline.name}</strong>{' '}
            {t('force_offline_body_suffix') || 'offline?'}
            <br /><br />
            {t('force_offline_warning_prefix') || 'Note: only the teammate themselves can '}
            <strong>{t('force_offline_warning_bold') || 'set their status back to online'}</strong>
            {t('force_offline_warning_suffix') || ' from their own account.'}
          </>
        )}
      </WarningModal>
    </div>
  );
};

export { DEFAULT_AVATARS, getAvatarSvg, getAvatarSrc };
export default LiveChatSettings;
